import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  closestCorners,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  type DragStartEvent,
  type DragOverEvent,
  type DragEndEvent,
  type CollisionDetection,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import { formatCents } from '@/lib/format';
import { client } from '@/lib/api';
import type { Item } from '@everplan/shared';
import StatusChip from '@/components/StatusChip';
import InlineEdit from '@/components/InlineEdit';
import SortableItemRow from '@/components/project-overview/SortableItemRow';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export type CategoryStatus = 'estimated' | 'quoted' | 'contracted';

interface CategoryData {
  id: string;
  name: string;
  estimatedCost: number | null;
  [key: string]: unknown;
}

interface CategorySummary {
  itemCount: number;
  bestimate: { min: number; max: number };
  contractedAmount: number;
  status: CategoryStatus;
}

interface CategoryWithItems {
  category: CategoryData;
  items: (Item & { _quotedMin?: number; _quotedMax?: number; _booked?: number })[];
  summary: CategorySummary;
}

type OverviewItem = Item & { _quotedMin?: number; _quotedMax?: number; _booked?: number };

const multiContainerCollision: CollisionDetection = (args) => {
  const dragType = args.active.data.current?.type;
  if (dragType === 'category') return closestCenter(args);
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) return pointerCollisions;
  return closestCorners(args);
};

interface ProjectOverviewTableProps {
  categories: CategoryWithItems[];
  projectId: string;
  onReorder?: (activeId: string, overId: string) => void;
  onSelectItem?: (itemId: string) => void;
  onItemDragStart?: () => void;
  onItemDragOver?: (event: DragOverEvent) => void;
  onItemDragEnd?: (event: DragEndEvent) => void;
  onItemDragCancel?: () => void;
}

export default function ProjectOverviewTable({ categories, projectId, onReorder, onSelectItem, onItemDragStart, onItemDragOver, onItemDragEnd, onItemDragCancel }: ProjectOverviewTableProps) {
  const [openCategories, setOpenCategories] = useState<Set<string>>(
    () => new Set(categories.map(({ category }) => category.id)),
  );
  const expandTimerRef = useRef<{ categoryId: string; timer: ReturnType<typeof setTimeout> } | null>(null);
  const [activeItem, setActiveItem] = useState<OverviewItem | null>(null);
  const queryClient = useQueryClient();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const updateCategory = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { name?: string; estimatedCost?: number | null } }) => {
      const res = await client.api.categories[':id'].$put({ param: { id }, json: data });
      if (!res.ok) throw new Error('Failed to update category');
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project', projectId] }),
  });

  const updateItem = useMutation({
    mutationFn: async ({ id, data }: {
      id: string;
      data: { name?: string; estimatedCost?: number | null; isSelectedForProject?: boolean };
    }) => {
      const res = await client.api.items[':id'].$put({ param: { id }, json: data });
      if (!res.ok) throw new Error('Failed to update item');
      return res.json();
    },
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: ['project', projectId] });
      const prev = queryClient.getQueryData<any>(['project', projectId]);
      if (prev) {
        const next = {
          ...prev,
          categories: prev.categories.map((cat: any) => ({
            ...cat,
            items: (cat.items ?? []).map((it: any) =>
              it.id === id ? { ...it, ...data } : it,
            ),
          })),
        };
        queryClient.setQueryData(['project', projectId], next);
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['project', projectId], ctx.prev);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['project', projectId] }),
  });

  const deleteItem = useMutation({
    mutationFn: async (id: string) => {
      const res = await client.api.items[':id'].$delete({ param: { id } });
      if (!res.ok) throw new Error('Failed to delete item');
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project', projectId] }),
  });

  const [itemToDelete, setItemToDelete] = useState<{ id: string; name: string } | null>(null);

  const toggleCategory = (id: string) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDragStart = (event: DragStartEvent) => {
    const type = event.active.data.current?.type;
    if (type === 'category') {
      const catId = event.active.id as string;
      setOpenCategories((prev) => {
        if (!prev.has(catId)) return prev;
        const next = new Set(prev);
        next.delete(catId);
        return next;
      });
    } else if (type === 'item') {
      const draggedId = event.active.id as string;
      const found = categories.flatMap((c) => c.items).find((i) => i.id === draggedId);
      setActiveItem(found ?? null);
      onItemDragStart?.();
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const type = event.active.data.current?.type;
    if (type !== 'item') return;

    const overData = event.over?.data.current;
    if (overData) {
      const targetCatId = overData.type === 'empty-category'
        ? overData.categoryId
        : overData.type === 'item'
          ? overData.categoryId
          : overData.type === 'category'
            ? (event.over?.id as string)
            : null;
      if (targetCatId && !openCategories.has(targetCatId)) {
        if (expandTimerRef.current?.categoryId !== targetCatId) {
          clearTimeout(expandTimerRef.current?.timer);
          expandTimerRef.current = {
            categoryId: targetCatId,
            timer: setTimeout(() => {
              setOpenCategories((prev) => new Set([...prev, targetCatId]));
            }, 400),
          };
        }
      }
    }

    onItemDragOver?.(event);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    clearTimeout(expandTimerRef.current?.timer);
    expandTimerRef.current = null;
    setActiveItem(null);

    const type = event.active.data.current?.type;
    if (type === 'category') {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      onReorder?.(active.id as string, over.id as string);
    } else if (type === 'item') {
      onItemDragEnd?.(event);
    }
  };

  const handleDragCancel = () => {
    clearTimeout(expandTimerRef.current?.timer);
    expandTimerRef.current = null;
    setActiveItem(null);
    onItemDragCancel?.();
  };

  return (
    <div className="rounded-xl border border-[var(--color-outline-variant)] bg-card shadow-ambient">
      <table className="w-full text-sm">
          <thead className="sticky top-44 z-10">
            <tr className="text-left">
              <th className="bg-card px-2 py-2 border-b border-[var(--color-outline-variant)]" style={{ width: '32px' }}></th>
              <th className="bg-card px-3 py-2 text-xs font-medium text-muted-foreground border-b border-[var(--color-outline-variant)]" style={{ width: '24%' }}>Item</th>
              <th className="bg-card px-3 py-2 text-xs font-medium text-muted-foreground border-b border-[var(--color-outline-variant)]" style={{ width: '17%' }}>Vendor</th>
              <th className="bg-card px-3 py-2 text-center text-xs font-medium text-muted-foreground border-b border-[var(--color-outline-variant)]" style={{ width: '13%' }}>Status</th>
              <th className="bg-card px-3 py-2 text-right text-xs font-medium text-muted-foreground border-b border-[var(--color-outline-variant)]" style={{ width: '13%' }}>Estimated</th>
              <th className="bg-card px-3 py-2 text-right text-xs font-medium text-muted-foreground border-b border-[var(--color-outline-variant)]" style={{ width: '13%' }}>Quoted</th>
              <th className="bg-card px-3 py-2 text-right text-xs font-medium text-muted-foreground border-b border-[var(--color-outline-variant)]" style={{ width: '13%' }}>Booked</th>
              <th className="bg-card px-3 py-2 border-b border-[var(--color-outline-variant)]" style={{ width: '40px' }}></th>
            </tr>
          </thead>
          <DndContext sensors={sensors} collisionDetection={multiContainerCollision} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
            <SortableContext items={categories.map((c) => c.category.id)} strategy={verticalListSortingStrategy}>
              {categories.map(({ category, items, summary }) => {
                const isOpen = openCategories.has(category.id);

                return (
                  <CategoryRows
                    key={category.id}
                    category={category}
                    items={items}
                    summary={summary}
                    isOpen={isOpen}
                    onToggle={() => toggleCategory(category.id)}
                    onSelectItem={onSelectItem ?? (() => {})}
                    onUpdateCategory={(data) => updateCategory.mutate({ id: category.id, data })}
                    onUpdateItem={(itemId, data) => updateItem.mutate({ id: itemId, data })}
                    onToggleSelected={(id, next) => updateItem.mutate({ id, data: { isSelectedForProject: next } })}
                    onRequestDeleteItem={(item) => setItemToDelete({ id: item.id, name: item.name })}
                  />
                );
              })}
            </SortableContext>
            <DragOverlay dropAnimation={null}>
              {activeItem && (
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="bg-card shadow-lg rounded border border-primary/20">
                      <td className="px-2 py-2 w-8" />
                      <td className="px-3 py-2 pl-5 font-medium text-foreground" style={{ width: '24%' }}>
                        <div className="flex items-center gap-2">
                          <GripVertical className="size-3.5 text-muted-foreground" />
                          {activeItem.name}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-foreground/70" style={{ width: '17%' }}>{activeItem.vendorContactName || '—'}</td>
                      <td className="px-3 py-2 text-center" style={{ width: '13%' }}><StatusChip status={activeItem.status} /></td>
                      <td colSpan={4} />
                    </tr>
                  </tbody>
                </table>
              )}
            </DragOverlay>
          </DndContext>
        </table>
      <AlertDialog open={!!itemToDelete} onOpenChange={(open) => { if (!open) setItemToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {itemToDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the item along with all its quotes, contracts, and payments. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (itemToDelete) deleteItem.mutate(itemToDelete.id);
                setItemToDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CategoryRows({
  category,
  items,
  summary,
  isOpen,
  onToggle,
  onSelectItem,
  onUpdateCategory,
  onUpdateItem,
  onToggleSelected,
  onRequestDeleteItem,
}: {
  category: CategoryData;
  items: OverviewItem[];
  summary: CategorySummary;
  isOpen: boolean;
  onToggle: () => void;
  onSelectItem: (id: string) => void;
  onUpdateCategory: (data: { name?: string; estimatedCost?: number | null }) => void;
  onUpdateItem: (itemId: string, data: { name?: string; estimatedCost?: number | null }) => void;
  onToggleSelected: (id: string, next: boolean) => void;
  onRequestDeleteItem: (item: Item) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.id,
    data: { type: 'category' as const },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tbody ref={setNodeRef} style={style}>
      {/* Category separator row */}
      <tr
        className="border-b border-[var(--color-outline-variant)] bg-[var(--color-surface-container-low)] cursor-pointer transition-colors hover:bg-[var(--color-surface-container)] group/cat"
        onClick={onToggle}
      >
        <td colSpan={4} className="px-3 py-2">
          <div className="flex items-center gap-2">
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing text-muted-foreground/0 group-hover/cat:text-muted-foreground/40 hover:!text-muted-foreground touch-none transition-colors"
              aria-label="Drag to reorder"
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="size-3.5" />
            </button>
            {isOpen ? (
              <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
            )}
            <InlineEdit
              value={category.name}
              onSave={(name) => onUpdateCategory({ name })}
              className="text-sm font-semibold"
            />
            <span className="text-xs text-muted-foreground">
              {summary.itemCount} {summary.itemCount === 1 ? 'item' : 'items'}
              {items.length > 0 && (
                (() => {
                  const selected = items.filter((i) => i.isSelectedForProject).length;
                  if (selected === 0) return ' · estimate';
                  return ` · ${selected} of ${items.length} selected`;
                })()
              )}
            </span>
          </div>
        </td>
        <td className="px-3 py-2 text-right text-xs tabular-nums text-foreground/70" onClick={(e) => e.stopPropagation()}>
          <InlineEdit
            value={String(category.estimatedCost ?? 0)}
            onSave={(v) => onUpdateCategory({ estimatedCost: v ? parseInt(v, 10) : null })}
            type="currency"
            className="text-xs tabular-nums"
            inputClassName="text-right w-24"
          />
        </td>
        <td className="px-3 py-2" />
        <td className="px-3 py-2 text-right text-xs tabular-nums text-green-600">
          {summary.contractedAmount > 0 ? formatCents(summary.contractedAmount) : '—'}
        </td>
        <td className="px-3 py-2" />
      </tr>

      {/* Item rows */}
      {isOpen && items.length > 0 && (
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          {items.map((item) => (
            <SortableItemRow
              key={item.id}
              item={item}
              categoryId={category.id}
              onSelectItem={onSelectItem}
              onUpdateItem={onUpdateItem}
              onToggleSelected={onToggleSelected}
              onRequestDeleteItem={onRequestDeleteItem}
            />
          ))}
        </SortableContext>
      )}

      {isOpen && items.length === 0 && (
        <EmptyCategoryDropZone categoryId={category.id} />
      )}
    </tbody>
  );
}

function EmptyCategoryDropZone({ categoryId }: { categoryId: string }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `empty-${categoryId}`,
    data: { type: 'empty-category' as const, categoryId },
  });

  return (
    <tr ref={setNodeRef}>
      <td
        colSpan={8}
        className={`px-3 py-3 pl-9 text-sm text-muted-foreground italic transition-colors ${
          isOver ? 'bg-primary/5' : ''
        }`}
      >
        {isOver ? 'Drop here' : 'No items yet'}
      </td>
    </tr>
  );
}
